import { EventsData, LiveEventData } from "../data/events";
import { isoWeekMonday, isoWeekTag, mondayOf } from "./isoWeek";

/**
 * Which event runs on a UTC day (MP-21, DESIGN §10.5): a dated set-piece event on its own days, else the weekly
 * rotation's kind for that week — kinds[(weeks since rotation.fromDay) mod kinds], so it needs no server, runs for as
 * long as the game is played and never puts the same kind in two weeks running. A week is clipped around a dated event
 * and dropped when fewer than minDays remain. Pure integer day maths, computed on demand (nothing is expanded at load).
 */

const DAYS_PER_WEEK = 7;
/** How far ahead `nextEventAfter` looks for the next start (a year and a bit: the gap between dated events is far less). */
const LOOKAHEAD_DAYS = 400;
/** A rotation instance id: `<kind>_<ISO year>w<ISO week>` (kind ids are lower-case letters: gen.py). */
const INSTANCE_ID = /^([a-z]+)_(\d{4})w(\d{2})$/;

/** The dated event covering `day`, or null. */
export function datedEventOn(data: EventsData, day: number): LiveEventData | null {
  return data.calendar.find((e) => e.startDay <= day && day < e.endDay) ?? null;
}

/** The rotation's week count of the week starting on Monday `monday` (0 = the first week), or -1 before the rotation. */
export function rotationIndex(data: EventsData, monday: number): number {
  const r = data.rotation;
  return r === null || monday < r.fromDay ? -1 : Math.floor((monday - r.fromDay) / DAYS_PER_WEEK);
}

/**
 * The rotation's event for the week starting on Monday `monday`: its kind, clipped to the days no dated event covers;
 * null before the rotation starts or when the clipped week is shorter than minDays.
 */
export function rotationWeek(data: EventsData, monday: number): LiveEventData | null {
  const r = data.rotation, index = rotationIndex(data, monday);
  if (r === null || index < 0) return null;
  let start = monday, end = monday + DAYS_PER_WEEK;
  for (const d of data.calendar) {
    if (d.startDay <= start && start < d.endDay) start = d.endDay;
    if ((d.startDay < end && end <= d.endDay) || (start < d.startDay && d.startDay < end)) end = Math.min(end, d.startDay);
  }
  if (end - start < r.minDays) return null;
  const n = r.kinds.length, kind = r.kinds[index % n];
  // Hunters' quarry turns with the kind's own weeks: each Hunters' Week names the next enemy on its list.
  const featured = kind.featured.length > 0 ? kind.featured[Math.floor(index / n) % kind.featured.length] : null;
  return {
    id: `${kind.id}_${isoWeekTag(monday)}`, kind: kind.id, name: kind.name, currency: kind.currency, blurb: kind.blurb,
    theme: kind.theme, art: kind.art, startDay: start, endDay: end, nodes: kind.nodes, sources: kind.sources, featured, recurring: true,
  };
}

/** The event running on UTC day `day`: the dated one there, else the rotation's week (null when neither runs). */
export function eventForUtcDay(data: EventsData, day: number): LiveEventData | null {
  const dated = datedEventOn(data, day);
  if (dated) return dated;
  const week = rotationWeek(data, mondayOf(day));
  return week && week.startDay <= day && day < week.endDay ? week : null;
}

/**
 * The next event to start after UTC day `day` (dated or a rotation week), or null when none starts within LOOKAHEAD_DAYS
 * of the later of `day` and the first event in the data (a clock far behind still names the first one).
 */
export function nextEventAfter(data: EventsData, day: number): LiveEventData | null {
  const first = Math.min(data.rotation?.fromDay ?? Infinity, ...data.calendar.map((e) => e.startDay));
  const from = Math.max(day + 1, Number.isFinite(first) ? first : day + 1);
  for (let d = from; d <= from + LOOKAHEAD_DAYS; d++) {
    const e = eventForUtcDay(data, d);
    if (e && e.startDay === d) return e;
  }
  return null;
}

/**
 * The Monday of a rotation instance id's week ("siege_2026w43" → 2026-10-19), or null for any other id. Any kind
 * matches, so an id from an older rotation (the weekly Proving Grounds of every week before MP-21) still ages out.
 */
export function rotationIdMonday(data: EventsData, id: string): number | null {
  if (data.calendar.some((e) => e.id === id)) return null;
  const m = INSTANCE_ID.exec(id);
  return m ? isoWeekMonday(Number(m[2]), Number(m[3])) : null;
}
